import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Building2, Clock, Coffee, ExternalLink } from 'lucide-react'
import { Button, ConfirmDialog, Spinner } from '@/components/ui'
import { cn } from '@/lib/cn'
import {
  useBreakEnd,
  useBreakStart,
  useClockIn,
  useClockOut,
  useEndDay,
  useMyAttendanceToday,
} from '@/features/attendance/hooks/useAttendance'
import { useMyCompanies } from '@/features/companies/hooks/useCompanies'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuthStore } from '@/stores/useAuthStore'
import type {
  MyAttendanceTodayCompanyEntry,
  MyAttendanceTodayShiftEntry,
} from '@/features/attendance/types/attendance.types'
import type {
  ShiftAssignment,
  ShiftAssignmentShiftRef,
} from '@/types/shift.types'
import { getAssignmentDateLabel } from '@/features/attendance/utils/shiftDate'
import {
  canAccessAttendanceSelfService,
  hasAttendanceEligibleAssignment,
  isAdminRole,
} from '@/features/attendance/utils/attendanceEligibility'
import { getLiveElapsedSeconds } from '@/features/attendance/utils/liveElapsed'
import { getClockInWindowMs } from '@/features/attendance/utils/clockInWindow'

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function formatCount(value: number, singular: string, plural = `${singular}s`): string {
  return `${value} ${value === 1 ? singular : plural}`
}

function formatTime(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso))
  } catch {
    return '—'
  }
}

function getShiftLabel(assignment: ShiftAssignment | null): string {
  if (!assignment) return 'Unscheduled'
  const shift =
    assignment.shiftId && typeof assignment.shiftId === 'object'
      ? assignment.shiftId
      : null
  return (shift as { name?: string } | null)?.name ?? 'Shift'
}

function getShiftWindow(assignment: ShiftAssignment | null): string | null {
  if (!assignment) return null
  const shift =
    assignment.shiftId && typeof assignment.shiftId === 'object'
      ? (assignment.shiftId as ShiftAssignmentShiftRef)
      : null
  const start = assignment.startTime ?? shift?.startTime
  const end = assignment.endTime ?? shift?.endTime
  if (!start || !end) return null
  return `${start}-${end}`
}

type ClockInAvailability = 'ready' | 'too-early' | 'ended'

function getClockInAvailability(
  shift: MyAttendanceTodayShiftEntry,
  nowMs: number,
): ClockInAvailability {
  if (!shift.assignment) return 'ended'
  const window = getClockInWindowMs(shift.assignment, shift.clockInWindow)
  if (!window) return 'ready'
  if (nowMs < window.opensMs) return 'too-early'
  if (!window.allowPastEnd && nowMs > window.closesMs) return 'ended'
  return 'ready'
}

export function AttendanceHeaderPill(): React.ReactElement | null {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  const { can, isLoading: permissionsLoading } = usePermissions()
  const roleSlug = useAuthStore((s) => s.user?.roleSlug)
  const canRead = can('attendance', 'read_self')
  const canCreate = can('attendance', 'create') && !isAdminRole(roleSlug)
  const enabled =
    canAccessAttendanceSelfService({ roleSlug, canRead }) && !permissionsLoading

  const { data: myCompanies = [] } = useMyCompanies()
  const { data: todayData, isLoading } = useMyAttendanceToday({ enabled })
  const todayEntries = useMemo(() => todayData?.data ?? [], [todayData?.data])
  const [nowMs, setNowMs] = useState(() => Date.now())

  const allShifts = useMemo(
    () => todayEntries.flatMap((entry) => entry.shifts),
    [todayEntries],
  )
  const activeShifts = useMemo(
    () => allShifts.filter((shift) => shift.attendance?.isActive),
    [allShifts],
  )
  const onBreakShifts = useMemo(
    () => activeShifts.filter((shift) => shift.attendance?.isOnBreak),
    [activeShifts],
  )
  const runningShifts = useMemo(
    () => activeShifts.filter((shift) => !shift.attendance?.isOnBreak),
    [activeShifts],
  )
  const prioritizedEntries = useMemo(() => {
    const priority = (entry: MyAttendanceTodayCompanyEntry): number => {
      if (entry.shifts.some((shift) => shift.attendance?.isActive && !shift.attendance?.isOnBreak)) return 0
      if (entry.shifts.some((shift) => shift.attendance?.isOnBreak)) return 1
      if (entry.shifts.some((shift) => !shift.attendance?.dayCompleted)) return 2
      return 3
    }

    return myCompanies
      .map((company) => ({
        company,
        entry: todayEntries.find((entry) => entry.companyId === company._id) ?? {
          companyId: company._id,
          shifts: [],
        },
      }))
      .filter(({ entry }) => entry.shifts.length > 0)
      .sort((a, b) => priority(a.entry) - priority(b.entry))
  }, [myCompanies, todayEntries])

  const longestElapsedSeconds = useMemo(() => {
    if (runningShifts.length === 0) return 0
    return runningShifts.reduce((max, shift) => {
      const elapsed = getLiveElapsedSeconds(
        shift.attendance,
        nowMs,
        todayData?.serverTime,
      )
      return elapsed > max ? elapsed : max
    }, 0)
  }, [nowMs, runningShifts, todayData?.serverTime])

  // Shifts not yet clocked in need the clock too, so the pill flips to
  // "Clock in" when the window opens rather than after a reload.
  const hasPendingShift = allShifts.some(
    (shift) => !shift.attendance?.isActive && shift.attendance?.dayCompleted !== true,
  )
  useEffect(() => {
    if (runningShifts.length === 0 && !hasPendingShift && !isOpen) return
    const interval = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [hasPendingShift, isOpen, runningShifts.length])

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node | null
      if (!wrapperRef.current || wrapperRef.current.contains(target)) return
      if (
        target instanceof Element &&
        target.closest('[role="alertdialog"], [role="dialog"]')
      ) {
        return
      }
      setIsOpen(false)
    }
    document.addEventListener('keydown', handleEscape)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  if (!enabled || myCompanies.length === 0) return null
  if (allShifts.length === 0) return null

  const displayElapsed = longestElapsedSeconds
    ? formatElapsed(longestElapsedSeconds)
    : null
  const activeCount = activeShifts.length
  const allDayCompleted =
    allShifts.length > 0 &&
    allShifts.every((shift) => shift.attendance?.dayCompleted)
  const clockInAvailable = allShifts.some(
    (shift) =>
      !shift.attendance?.isActive &&
      shift.attendance?.dayCompleted !== true &&
      hasAttendanceEligibleAssignment(shift.assignment) &&
      getClockInAvailability(shift, nowMs) === 'ready',
  )

  const statusDotClass =
    runningShifts.length > 0
      ? 'bg-green-500'
      : onBreakShifts.length > 0
        ? 'bg-amber-500'
        : allDayCompleted
          ? 'bg-gray-400'
          : 'bg-gray-300'

  const pillLabel = (() => {
    if (runningShifts.length > 0) {
      if (runningShifts.length > 1) return `${activeCount} active · ${displayElapsed}`
      return displayElapsed ?? 'Active'
    }
    if (onBreakShifts.length > 0) {
      return onBreakShifts.length > 1
        ? `${onBreakShifts.length} on break`
        : 'On break'
    }
    if (allDayCompleted) return 'Day complete'
    return clockInAvailable ? 'Clock in' : 'No active shift'
  })()

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        aria-label="Today's attendance"
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-white/75 bg-white/85 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:hover:bg-gray-800',
          runningShifts.length > 0 &&
            'border-green-300 bg-green-50 text-green-900 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-100',
          runningShifts.length === 0 &&
            onBreakShifts.length > 0 &&
            'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100',
        )}
      >
        <span
          className={cn(
            'inline-flex h-2 w-2 rounded-full',
            statusDotClass,
            runningShifts.length > 0 && 'animate-pulse',
          )}
          aria-hidden
        />
        <Clock className="h-3.5 w-3.5" aria-hidden />
        <span>{pillLabel}</span>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-[min(360px,92vw)] overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.14)] backdrop-blur dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/30"
          role="menu"
          aria-label="Today's attendance"
        >
          <div className="border-b border-gray-100 px-3.5 py-3 dark:border-gray-800">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  <span
                    className={cn(
                      'inline-flex h-2 w-2 rounded-full',
                      statusDotClass,
                      runningShifts.length > 0 && 'animate-pulse',
                    )}
                    aria-hidden
                  />
                  Shift status
                </div>
                <p className="mt-1 truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
                  {activeCount > 0
                    ? `${formatCount(activeCount, 'shift')} active`
                    : allDayCompleted
                      ? 'All tracked shifts complete'
                      : 'No active shift'}
                </p>
              </div>
              <span className="max-w-full shrink-0 truncate rounded-full bg-gray-900 px-2.5 py-1 text-xs font-semibold text-white dark:bg-gray-100 dark:text-gray-900">
                {pillLabel}
              </span>
            </div>
          </div>

          <div className="max-h-[min(340px,64vh)] space-y-1.5 overflow-y-auto p-2">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : prioritizedEntries.length > 0 ? (
              prioritizedEntries.map(({ company, entry }) => (
                <CompactAttendanceCompany
                  key={company._id}
                  entry={entry}
                  companyName={company.name}
                  canCreate={canCreate}
                  serverTime={todayData?.serverTime}
                />
              ))
            ) : (
              <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-sm dark:border-gray-800 dark:bg-gray-800/55">
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  No scheduled shifts today
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Open the full page to review previous records.
                </p>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 px-3.5 py-2.5 text-right dark:border-gray-800">
            <Link
              to="/my-attendance"
              className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-primary transition-colors hover:text-primary/80"
              onClick={() => setIsOpen(false)}
            >
              Open My Attendance
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

interface CompactAttendanceCompanyProps {
  entry: MyAttendanceTodayCompanyEntry
  companyName: string
  canCreate: boolean
  serverTime?: string
}

function CompactAttendanceCompany({
  entry,
  companyName,
  canCreate,
  serverTime,
}: CompactAttendanceCompanyProps): React.ReactElement {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-2 dark:border-gray-800 dark:bg-gray-800/55">
      <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 px-1">
        <Building2 className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
        <span className="truncate text-xs font-semibold text-gray-800 dark:text-gray-100">
          {companyName}
        </span>
        {entry.shifts.length > 1 && (
          <span className="shrink-0 text-[11px] text-gray-400">
            {entry.shifts.length} shifts
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {entry.shifts.map((shift) => (
          <CompactAttendanceShift
            key={shift.shiftAssignmentId}
            shift={shift}
            companyId={entry.companyId}
            companyName={companyName}
            canCreate={canCreate}
            serverTime={serverTime}
          />
        ))}
      </div>
    </div>
  )
}

interface CompactAttendanceShiftProps {
  shift: MyAttendanceTodayShiftEntry
  companyId: string
  companyName: string
  canCreate: boolean
  serverTime?: string
}

function CompactAttendanceShift({
  shift,
  companyId,
  companyName,
  canCreate,
  serverTime,
}: CompactAttendanceShiftProps): React.ReactElement {
  const { shiftAssignmentId, assignment, attendance } = shift
  const clockInMutation = useClockIn()
  const breakStartMutation = useBreakStart()
  const breakEndMutation = useBreakEnd()
  const clockOutMutation = useClockOut()
  const endDayMutation = useEndDay()
  const [confirmClockOutOpen, setConfirmClockOutOpen] = useState(false)
  const [confirmEndDayOpen, setConfirmEndDayOpen] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())

  const isActive = attendance?.isActive ?? false
  const isOnBreak = attendance?.isOnBreak ?? false
  const dayCompleted = attendance?.dayCompleted === true
  const isRunning = isActive && !isOnBreak

  // Tick while running (live elapsed) and while waiting to clock in, so the
  // clock-in window opens on time.
  const needsClock = isRunning || (!isActive && !dayCompleted)
  useEffect(() => {
    if (!needsClock) return
    const interval = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [needsClock])

  const displayElapsed = getLiveElapsedSeconds(attendance, nowMs, serverTime)
  const anyPending =
    clockInMutation.isPending ||
    breakStartMutation.isPending ||
    breakEndMutation.isPending ||
    clockOutMutation.isPending ||
    endDayMutation.isPending
  const shiftLabel = useMemo(() => getShiftLabel(assignment), [assignment])
  const shiftWindow = useMemo(() => getShiftWindow(assignment), [assignment])
  const shiftDateLabel = useMemo(
    () => getAssignmentDateLabel(assignment),
    [assignment],
  )
  const clockInAvailability = getClockInAvailability(shift, nowMs)
  const canClockInThisShift =
    hasAttendanceEligibleAssignment(assignment) &&
    clockInAvailability === 'ready'

  const handleClockIn = (): void => {
    if (!canCreate || !assignment) return
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    clockInMutation.mutate({
      companyId,
      payload: { shiftAssignmentId, timezone },
    })
  }

  const confirmClockOut = (): void => {
    if (!canCreate) return
    clockOutMutation.mutate(companyId, {
      onSettled: () => setConfirmClockOutOpen(false),
    })
  }

  const confirmEndDay = (): void => {
    if (!canCreate) return
    endDayMutation.mutate(companyId, {
      onSettled: () => setConfirmEndDayOpen(false),
    })
  }

  const toneClass = dayCompleted
    ? 'border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-900/70'
    : isRunning
      ? 'border-emerald-100 bg-emerald-50/80 dark:border-emerald-900/40 dark:bg-emerald-950/20'
      : isOnBreak
        ? 'border-amber-100 bg-amber-50/80 dark:border-amber-900/40 dark:bg-amber-950/20'
        : 'border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-900/70'
  const dotClass = dayCompleted
    ? 'bg-gray-400'
    : isRunning
      ? 'bg-emerald-500'
      : isOnBreak
        ? 'bg-amber-500'
        : 'bg-primary/70'
  const headline = dayCompleted
    ? 'Done'
    : isRunning
      ? formatElapsed(displayElapsed)
      : isOnBreak
        ? 'On break'
        : clockInAvailability === 'ended'
          ? 'Shift ended'
          : clockInAvailability === 'too-early'
            ? 'Not started'
            : 'Ready'

  return (
    <div className={cn('rounded-xl border px-2.5 py-2', toneClass)}>
      <div className="flex flex-wrap items-start gap-2">
        <span
          className={cn(
            'h-2 w-2 shrink-0 rounded-full',
            dotClass,
            isRunning && 'animate-pulse',
          )}
          aria-hidden
        />
        <div className="min-w-[9rem] flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
            <span className="truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
              {shiftLabel}
            </span>
            {shiftWindow && (
              <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                {shiftWindow}
              </span>
            )}
            {shiftDateLabel && (
              <span className="max-w-full truncate rounded-full border border-primary/15 bg-primary/5 px-1.5 py-px text-[10px] font-semibold text-primary dark:border-primary/30 dark:bg-primary/10">
                {shiftDateLabel}
              </span>
            )}
          </div>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {headline}
            {isActive && attendance?.clockIn ? ` · In ${formatTime(attendance.clockIn)}` : ''}
          </p>
        </div>

        {canCreate && !dayCompleted && (isActive || canClockInThisShift) && (
          <CompactActions
            isActive={isActive}
            isOnBreak={isOnBreak}
            anyPending={anyPending}
            canClockIn={canClockInThisShift}
            onClockIn={handleClockIn}
            onBreakStart={() => breakStartMutation.mutate({ companyId, payload: {} })}
            onBreakEnd={() => breakEndMutation.mutate(companyId)}
            onClockOut={() => setConfirmClockOutOpen(true)}
            onEndDay={() => setConfirmEndDayOpen(true)}
          />
        )}
      </div>

      <ConfirmDialog
        open={confirmClockOutOpen}
        onClose={() => setConfirmClockOutOpen(false)}
        onConfirm={confirmClockOut}
        title="Clock out?"
        message={`Pause ${shiftLabel} at ${companyName}. Worked so far: ${formatElapsed(displayElapsed)}.`}
        confirmLabel="Clock out"
        variant="default"
        confirmLoading={clockOutMutation.isPending}
      />
      <ConfirmDialog
        open={confirmEndDayOpen}
        onClose={() => setConfirmEndDayOpen(false)}
        onConfirm={confirmEndDay}
        title="End shift?"
        message={`This finalizes ${shiftLabel} at ${companyName}. Worked: ${formatElapsed(displayElapsed)}.`}
        confirmLabel="End shift"
        variant="danger"
        confirmLoading={endDayMutation.isPending}
      />
    </div>
  )
}

interface CompactActionsProps {
  isActive: boolean
  isOnBreak: boolean
  anyPending: boolean
  canClockIn: boolean
  onClockIn: () => void
  onBreakStart: () => void
  onBreakEnd: () => void
  onClockOut: () => void
  onEndDay: () => void
}

function CompactActions({
  isActive,
  isOnBreak,
  anyPending,
  canClockIn,
  onClockIn,
  onBreakStart,
  onBreakEnd,
  onClockOut,
  onEndDay,
}: CompactActionsProps): React.ReactElement {
  if (!isActive) {
    return (
      <Button
        type="button"
        variant="primary"
        size="sm"
        className="h-8 min-h-8 rounded-lg px-3"
        onClick={onClockIn}
        disabled={anyPending || !canClockIn}
      >
        Clock in
      </Button>
    )
  }

  if (isOnBreak) {
    return (
      <Button
        type="button"
        variant="primary"
        size="sm"
        className="h-8 min-h-8 rounded-lg px-3"
        onClick={onBreakEnd}
        disabled={anyPending}
      >
        <Coffee className="mr-1 h-3.5 w-3.5" aria-hidden />
        End
      </Button>
    )
  }

  return (
    <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="h-8 min-h-8 rounded-lg px-2.5"
        onClick={onBreakStart}
        disabled={anyPending}
      >
        Break
      </Button>
      <Button
        type="button"
        variant="danger"
        size="sm"
        className="h-8 min-h-8 rounded-lg px-2.5"
        onClick={onClockOut}
        disabled={anyPending}
      >
        Out
      </Button>
      <button
        type="button"
        className="px-1 text-[11px] font-semibold text-red-600 hover:text-red-700 disabled:opacity-50 dark:text-red-400"
        onClick={onEndDay}
        disabled={anyPending}
      >
        End
      </button>
    </div>
  )
}

AttendanceHeaderPill.displayName = 'AttendanceHeaderPill'
